import { useEffect, useRef, useState } from 'react';
import './landing.css';
import { supabase } from './lib/supabase';
import { SalifyLogo } from './Logo';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
type AuthView = 'signup' | 'login';
type EmailState = 'idle' | 'loading' | 'sent';

interface AuthModalProps {
  open: boolean;
  view?: AuthView;
  initialError?: string | null;
  onClose: () => void;
  onNavigate?: (page: 'terms' | 'privacy') => void;
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332Z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 814 1000" fill="currentColor" aria-hidden>
      <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.3-155.5-98c-58.7-73.3-105-196.3-105-312.1 0-187.3 122.1-286 242.6-286 73 0 133.5 48.3 178.6 48.3 43.1 0 110.8-50.9 193.8-50.9 32.7 0 117.8 3.2 179.3 77.5zm-338.5-166.1c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d="M18 9a9 9 0 1 0-10.406 8.89v-6.29H5.309V9h2.285V7.017c0-2.255 1.343-3.502 3.4-3.502.984 0 2.014.175 2.014.175V5.91h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.399 2.6H10.406v6.29A9.002 9.002 0 0 0 18 9Z" fill="#1877F2" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <rect x="1.5" y="4" width="15" height="11" rx="1.5" />
      <path d="m1.5 5 7.5 5.5L16.5 5" />
    </svg>
  );
}

export function AuthModal({ open, view: initialView = 'signup', initialError, onClose, onNavigate }: AuthModalProps) {
  const [authView, setAuthView] = useState<AuthView>(initialView);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [emailState, setEmailState] = useState<EmailState>('idle');
  const [authLoading, setAuthLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 5000);
  };

  useEffect(() => {
    if (open && initialError) showToast(initialError);
  }, [open, initialError]);

  if (!open) return null;

  const switchView = (v: AuthView) => {
    setAuthView(v);
    setShowEmail(false);
    setEmail('');
    setPassword('');
    setPasswordError('');
    setEmailState('idle');
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  const resetCaptcha = () => {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  };

  const handleGoogleAuth = async () => {
    setAuthLoading(true);
    // Persist user intent across the OAuth redirect so App.tsx can detect
    // a new-user trying to "log in" and block the implicit Google account creation.
    try { localStorage.setItem('salifly_oauth_mode', authView); } catch { /* ignore */ }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      try { localStorage.removeItem('salifly_oauth_mode'); } catch { /* ignore */ }
      showToast(error.message);
      setAuthLoading(false);
    }
  };

  const handleEmailSubmit = async () => {
    if (!email.includes('@')) return;
    setPasswordError('');
    if (authView === 'signup' && password.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }
    if (password.length === 0) {
      setPasswordError('Please enter your password.');
      return;
    }
    setEmailState('loading');
    if (authView === 'signup') {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) {
        showToast(error.message);
        setEmailState('idle');
        resetCaptcha();
      } else {
        setEmailState('sent');
      }
    } else {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      if (error) {
        showToast(error.message);
        setEmailState('idle');
        resetCaptcha();
      } else {
        onClose();
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!email.includes('@')) {
      showToast('Enter your email address first.');
      return;
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}?reset=1`,
      ...(captchaToken ? { captchaToken } : {}),
    });
    if (error) {
      showToast(error.message);
      resetCaptcha();
    } else {
      showToast('Password reset link sent — check your inbox.');
    }
  };

  const handleComingSoon = () => showToast('Coming soon. Google sign-in is available now.');

  return (
    <>
      {toast && <div className="lp-toast" role="status">{toast}</div>}
      <div className="auth-overlay" onClick={onClose}>
        <div className="auth-modal fade-in" onClick={e => e.stopPropagation()}>
          <button className="auth-close" onClick={onClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 1l12 12M13 1L1 13" />
            </svg>
          </button>

          <div className="auth-logo">
            <SalifyLogo size={26} fontSize={22} gap={8} />
          </div>

          <div className="auth-tabs">
            <button
              className={`auth-tab${authView === 'signup' ? ' auth-tab--active' : ''}`}
              onClick={() => switchView('signup')}
            >
              Sign up
            </button>
            <button
              className={`auth-tab${authView === 'login' ? ' auth-tab--active' : ''}`}
              onClick={() => switchView('login')}
            >
              Log in
            </button>
          </div>

          <h2 className="auth-heading">
            {authView === 'signup' ? 'Create your account' : 'Welcome back'}
          </h2>
          <p className="auth-sub">
            {authView === 'signup'
              ? 'Start closing more deals with smarter outreach.'
              : 'Continue where you left off.'}
          </p>

          <div className="auth-providers">
            <button className="lp-provider-btn" onClick={handleGoogleAuth} disabled={authLoading}>
              <GoogleIcon /> Continue with Google
            </button>
            <button className="lp-provider-btn" onClick={handleComingSoon}>
              <AppleIcon /> Continue with Apple
            </button>
            <button className="lp-provider-btn" onClick={handleComingSoon}>
              <FacebookIcon /> Continue with Facebook
            </button>
          </div>

          <div className="auth-divider"><span>or</span></div>

          {emailState === 'sent' ? (
            <div className="auth-email-sent">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
                <rect x="1.5" y="4" width="17" height="13" rx="2" />
                <path d="m1.5 5.5 8.5 6 8.5-6" />
              </svg>
              <p>Check <strong>{email}</strong> to verify your account, then log in.</p>
              <button className="auth-link" onClick={() => { setEmailState('idle'); setEmail(''); setPassword(''); }}>
                Use a different email
              </button>
            </div>
          ) : !showEmail ? (
            <button className="lp-provider-btn" onClick={() => setShowEmail(true)}>
              <EmailIcon /> Continue with email
            </button>
          ) : (
            <div className="auth-email-form">
              <input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="auth-input"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
              />
              <input
                type="password"
                placeholder={authView === 'signup' ? 'Create a password (8+ chars)' : 'Password'}
                value={password}
                onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                className="auth-input"
                onKeyDown={e => e.key === 'Enter' && handleEmailSubmit()}
              />
              {passwordError && <p className="auth-field-error">{passwordError}</p>}
              {TURNSTILE_SITE_KEY && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onExpire={resetCaptcha}
                  onError={resetCaptcha}
                  options={{ theme: 'dark', size: 'flexible' }}
                />
              )}
              <button
                className="btn btn-primary auth-submit"
                onClick={handleEmailSubmit}
                disabled={
                  !email.includes('@') ||
                  emailState === 'loading' ||
                  (!!TURNSTILE_SITE_KEY && !captchaToken)
                }
              >
                {emailState === 'loading' ? 'Please wait…' : authView === 'signup' ? 'Create account →' : 'Log in →'}
              </button>
              {authView === 'login' && (
                <div className="auth-email-secondary">
                  <button className="auth-link" onClick={handleForgotPassword}>Forgot password?</button>
                </div>
              )}
            </div>
          )}

          <p className="auth-legal">
            By continuing you agree to Salifly's{' '}
            <button className="auth-link auth-link-btn" onClick={() => { onClose(); onNavigate?.('terms'); }}>Terms</button>
            {' '}and{' '}
            <button className="auth-link auth-link-btn" onClick={() => { onClose(); onNavigate?.('privacy'); }}>Privacy Policy</button>.
          </p>
        </div>
      </div>
    </>
  );
}
